import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { AUTH_NAVIGATION_EVENT } from './api/config';
import theme from './theme';
import DashboardLayout from './components/DashboardLayout';
import HealthTicker from './components/HealthTicker';


// Feature mapping for route protection
// This ensures that users can only access features they have permission for
// If a user tries to access a route without the required feature, they get redirected to dashboard
const FEATURE_ROUTE_MAP = {
  '/locations': 'locations',
  '/bins': 'bins',
  '/inventory': 'inventory',
  '/items': 'items',
  '/sales-orders': 'salesorder',
  '/dropship-sales-orders': 'dropshipSalesOrders',
  '/sales-order-list': 'salesOrderList',
  '/bulk-sales-order-priority': 'bulkSopriority',

  '/inbound-shipment': 'inbound',
  '/bin-count-records': 'bincount',
  '/bin-adjustment-records': 'binAdjustment',
  '/bin-transfer': 'binTransfer',
  '/inbound-data-records': 'inbound',
  '/homedepot-products-errors': 'homedepotProductsErrors',
  '/ltl-dashboard': 'ltlDashboard',
  '/ltl-pick-progress': 'ltlPickProgress',
  '/ltl-pallet-map': 'ltlDashboard',
  '/regular-pick-progress': 'regularPickProgress',
  '/all-scraper-itemdata': 'masterScraperData',
  '/fedex-files': 'fedexFileUpload',
  '/fedex-shipping-analysis': 'fedexShippingAnalysis',
  '/price-comparison': 'marketPlacePrice',
  '/marketplace-price-update': 'UpdateMarketplacePricing',
  '/netsuite-setup': 'locations', // Uses locations feature for NetSuite access
  '/item-sales-by-customer': 'itemSalesByCus',
  '/item-sales-report': 'itemSalesByCus',
  '/insufficient-inventory': 'ltlInsufficientInventory',
  '/ship-confirm-by-status': 'regularDashboard',
  '/ship-tracking': 'regularDashboard',
  '/amazon-amxl-dashboard': 'amAmxlDashboard',
  '/ats-dashboard': 'amAtsDashboard',
  '/ups-dashboard': 'amUpsDashboard',
  '/ship-errors': 'shippingLabelErrors'
};

// External dashboard URL - dashboard route redirects here ok
const EXTERNAL_DASHBOARD_URL = import.meta.env.VITE_PRIORITY_DASHBOARD_URL || 'https://dashboard.jyswms.com/priority-dashboard';

// Import main app components directly
import Locations from './pages/Locations';
import Bins from './pages/Bins';
import Admins from './pages/Admins';
import Inventory from './pages/Inventory';
import Items from './pages/Items';
import SalesOrders from './pages/SalesOrders';
import SalesOrderDetails from './pages/SalesOrderDetails';
import DropshipSalesOrders from './pages/DropshipSalesOrders';
import DropshipSalesOrderDetails from './pages/DropshipSalesOrderDetails';
import SalesOrderList from './pages/SalesOrderList';
import LtlPickProgress from './pages/LtlPickProgress';
import RegularPickProgress from './pages/RegularPickProgress';
import InboundShipment from './pages/InboundShipment';
import BinCountRecords from './pages/BinCountRecords';
import WmsAiUsers from './pages/WmsAiUsers';
import BinAdjustment from './pages/BinAdjustment';
import BinTransfer from './pages/BinTransfer';
import InboundDataRecords from './pages/InboundDataRecords';
import NetsuiteSetup from './pages/NetsuiteSetup';
import HomedepotProductsErrors from './pages/HomedepotProductsErrors';
import LtlDashboard from './pages/LtlDashboard';
import LtlPalletMap from './pages/LtlPalletMap';

import AmazonAmxlDashboard from './pages/AmazonAmxlDashboard';
import AtsDashboard from './pages/AtsDashboard';
import UpsDashboard from './pages/UpsDashboard';
import PartsDashboard from './pages/PartsDashboard';
import UserPickStatus from './pages/UserPickStatus';
import FedexFiles from './pages/FedexFiles';
import AllScraperItemData from './pages/AllScraperItemData';
import Errors from './pages/Errors';
import ShipErrors from './pages/ShipErrors';

import FedexShippingAnalysis from './pages/FedexShippingAnalysis';
import PriceComparison from './pages/PriceComparison';
import ItemSalesByCustomer from './pages/ItemSalesByCustomer';
import ItemSalesReport from './pages/ItemSalesReport';
import InsufficientInventory from './pages/InsufficientInventory';
import SubmittedPalletList from './pages/SubmittedPalletList';
import ShipConfirmByStatus from './pages/ShipConfirmByStatus';
import Roles from './pages/Roles';
import ContainerLabel from './pages/ContainerLabel';
import BulkSalesOrderPriority from './pages/BulkSalesOrderPriority';
import MarketplacePriceUpdate from './pages/MarketplacePriceUpdate';
import SystemHealthPage from './pages/SystemHealthPage';
import ShipTracking from './pages/ShipTracking';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, isFullyAuthenticated } = useAuth();

  // If not authenticated at all, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated but not fully authenticated, redirect to 2FA login
  if (!isFullyAuthenticated) {
    return <Navigate to="/2fa-login" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { isAuthenticated, isFullyAuthenticated } = useAuth();
  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';

  // If not authenticated at all, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated but not fully authenticated, redirect to 2FA login
  if (!isFullyAuthenticated) {
    return <Navigate to="/2fa-login" replace />;
  }

  // If not a super admin, redirect to dashboard
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Feature-protected route component
// This component ensures that users can only access features they have permission for
// If a user tries to access a route without the required feature, they get redirected to dashboard
const FeatureProtectedRoute = ({ children, requiredFeature }) => {
  const { isAuthenticated, isFullyAuthenticated, features } = useAuth();
  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';

  // If not authenticated at all, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated but not fully authenticated, redirect to 2FA login
  if (!isFullyAuthenticated) {
    return <Navigate to="/2fa-login" replace />;
  }

  // Super admins can access all feature-gated screens
  if (isSuperAdmin) {
    return children;
  }

  // If no features available, redirect to dashboard
  if (!features || typeof features !== 'object') {
    console.warn(`FeatureProtectedRoute: No features available for ${requiredFeature}. Features object:`, features);
    return <Navigate to="/dashboard" replace />;
  }

  // Check if the required feature is enabled
  const isEnabled =
    Boolean(features[requiredFeature]) ||
    (requiredFeature === 'UpdateMarketplacePricing' && Boolean(features['marketplacePriceUpdate'])) ||
    (requiredFeature === 'marketplacePriceUpdate' && Boolean(features['UpdateMarketplacePricing']));

  if (!isEnabled) {
    console.warn(`FeatureProtectedRoute: Feature "${requiredFeature}" is not enabled. Current features:`, features);
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Component that embeds the external priority dashboard inside the app layout (sidebar stays visible)

// Returns current date in US Eastern timezone in the same format as the dashboard
const DashboardDateDisplay = () => {
  const now = new Date();
  const easternDate = new Date(
    now.toLocaleString('en-US', { timeZone: 'America/New_York' })
  );
  return easternDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'America/New_York',
  });
};

const ExternalDashboardRedirect = () => {
  return (
    <DashboardLayout>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Dashboard header row: title+date (left) and API health ticker (right) */}
        <Box
          className="page-header-title-row"
          sx={{
            flexShrink: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1.5rem',
            px: { xs: 1, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            boxSizing: 'border-box',
            minWidth: 0,
          }}
        >
          {/* Left: Title and date */}
          <Box
            className="page-header-title-block"
            sx={{ flex: '0 0 auto', minWidth: 0 }}
          >
            <Box
              component="h1"
              sx={{
                fontSize: 'clamp(1rem, 3vw, 1.35rem)',
                fontWeight: 700,
                letterSpacing: '0.02em',
                margin: 0,
                marginBottom: '0.25rem',
                lineHeight: 1.25,
                color: '#1a202c',
                whiteSpace: 'nowrap',
              }}
            >
              JYSWMS – DAILY ORDERS DASHBOARD
            </Box>
            <Box
              component="p"
              sx={{
                margin: 0,
                color: '#555',
                fontSize: '0.875rem',
                lineHeight: 1.4,
              }}
            >
              {DashboardDateDisplay()}
            </Box>
          </Box>

          {/* Right: scrolling API health ticker */}
          <Box
            className="hs-ticker-mount"
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: '780px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <HealthTicker />
          </Box>
        </Box>

        {/* Iframe wrapper clips the iframe's duplicate title+date row */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <iframe
            src={EXTERNAL_DASHBOARD_URL}
            title="JYSWMS Priority Dashboard"
            style={{
              width: '100%',
              height: 'calc(100% + 88px)',
              marginTop: '-88px',
              border: 'none',
              display: 'block',
            }}
          />
        </Box>
      </Box>
    </DashboardLayout>
  );
};

// Navigation handler component with feature-based security
const NavigationHandler = () => {
  const navigate = useNavigate();
  const { features } = useAuth();

  useEffect(() => {
    const handleAuthNavigation = (event) => {
      const { path, search } = event.detail;

      // Check if the target path requires a specific feature
      const requiredFeature = FEATURE_ROUTE_MAP[path];

      if (requiredFeature && features && !features[requiredFeature]) {
        // User doesn't have access to this feature, redirect to dashboard
        console.warn(`NavigationHandler: Unauthorized navigation attempt to ${path}, redirecting to dashboard`);
        navigate('/dashboard', { replace: true });
        return;
      }

      navigate(path + (search || ''), { replace: true });
    };

    window.addEventListener(AUTH_NAVIGATION_EVENT, handleAuthNavigation);
    return () => window.removeEventListener(AUTH_NAVIGATION_EVENT, handleAuthNavigation);
  }, [navigate, features]);

  return null;
};

const MainApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NavigationHandler />
        <Routes>
          {/* Main app routes - all protected */}
          <Route path="/dashboard" element={
            <PrivateRoute>
              <ExternalDashboardRedirect />
            </PrivateRoute>
          } />
          <Route path="/system-health" element={
            <PrivateRoute>
              <SystemHealthPage />
            </PrivateRoute>
          } />
          <Route path="/locations" element={
            <FeatureProtectedRoute requiredFeature="locations">
              <Locations />
            </FeatureProtectedRoute>
          } />
          <Route path="/bins" element={
            <FeatureProtectedRoute requiredFeature="bins">
              <Bins />
            </FeatureProtectedRoute>
          } />
          <Route path="/admins" element={
            <SuperAdminRoute><Admins /></SuperAdminRoute>
          } />
          <Route path="/inventory" element={
            <FeatureProtectedRoute requiredFeature="inventory">
              <Inventory />
            </FeatureProtectedRoute>
          } />
          <Route path="/inventory/:itemKey" element={<Navigate to="/inventory" replace />} />
          <Route path="/items" element={
            <FeatureProtectedRoute requiredFeature="items">
              <Items />
            </FeatureProtectedRoute>
          } />
          <Route path="/sales-orders" element={
            <FeatureProtectedRoute requiredFeature="salesorder">
              <SalesOrders />
            </FeatureProtectedRoute>
          } />
          <Route path="/sales-orders/:transactionId" element={
            <FeatureProtectedRoute requiredFeature="salesorder">
              <SalesOrderDetails />
            </FeatureProtectedRoute>
          } />
          <Route path="/dropship-sales-orders" element={
            <FeatureProtectedRoute requiredFeature="dropshipSalesOrders">
              <DropshipSalesOrders />
            </FeatureProtectedRoute>
          } />
          <Route path="/dropship-sales-orders/:transactionId" element={
            <FeatureProtectedRoute requiredFeature="dropshipSalesOrders">
              <DropshipSalesOrderDetails />
            </FeatureProtectedRoute>
          } />
          <Route path="/sales-order-list" element={
            <FeatureProtectedRoute requiredFeature="salesOrderList">
              <SalesOrderList />
            </FeatureProtectedRoute>
          } />
          <Route path="/inbound-shipment" element={
            <FeatureProtectedRoute requiredFeature="inbound">
              <InboundShipment />
            </FeatureProtectedRoute>
          } />
          <Route path="/bin-count-records" element={
            <FeatureProtectedRoute requiredFeature="bincount">
              <BinCountRecords />
            </FeatureProtectedRoute>
          } />
          <Route path="/bin-adjustment-records" element={
            <FeatureProtectedRoute requiredFeature="binAdjustment">
              <BinAdjustment />
            </FeatureProtectedRoute>
          } />
          <Route path="/bin-transfer" element={
            <FeatureProtectedRoute requiredFeature="binTransfer">
              <BinTransfer />
            </FeatureProtectedRoute>
          } />
          <Route path="/inbound-data-records" element={
            <FeatureProtectedRoute requiredFeature="inbound">
              <InboundDataRecords />
            </FeatureProtectedRoute>
          } />
          <Route path="/wms-ai-users" element={
            <SuperAdminRoute><WmsAiUsers /></SuperAdminRoute>
          } />
          <Route path="/roles" element={
            <SuperAdminRoute><Roles /></SuperAdminRoute>
          } />
          <Route path="/homedepot-products-errors" element={
            <FeatureProtectedRoute requiredFeature="homedepotProducts">
              <HomedepotProductsErrors />
            </FeatureProtectedRoute>
          } />
          <Route path="/all-scraper-itemdata" element={
            <FeatureProtectedRoute requiredFeature="masterScraperData">
              <AllScraperItemData />
            </FeatureProtectedRoute>
          } />

          <Route path="/ltl-dashboard" element={
            <FeatureProtectedRoute requiredFeature="ltlDashboard">
              <LtlDashboard />
            </FeatureProtectedRoute>
          } />
          <Route path="/ltl-pallet-map" element={
            <FeatureProtectedRoute requiredFeature="ltlDashboard">
              <LtlPalletMap />
            </FeatureProtectedRoute>
          } />
          <Route path="/ltl-pick-progress" element={
            <FeatureProtectedRoute requiredFeature="ltlPickProgress">
              <LtlPickProgress />
            </FeatureProtectedRoute>
          } />
          <Route path="/regular-pick-progress" element={
            <FeatureProtectedRoute requiredFeature="regularPickProgress">
              <RegularPickProgress />
            </FeatureProtectedRoute>
          } />
          <Route path="/parts-dashboard" element={
            <FeatureProtectedRoute requiredFeature="PartsDashboard">
              <PartsDashboard />
            </FeatureProtectedRoute>
          } />
          <Route path="/user-pick-status" element={
            <FeatureProtectedRoute requiredFeature="userPickStatus">
              <UserPickStatus />
            </FeatureProtectedRoute>
          } />

          <Route path="/fedex-shipping-analysis" element={
            <FeatureProtectedRoute requiredFeature="fedexShippingAnalysis">
              <FedexShippingAnalysis />
            </FeatureProtectedRoute>
          } />
          <Route path="/price-comparison" element={
            <FeatureProtectedRoute requiredFeature="marketPlacePrice">
              <PriceComparison />
            </FeatureProtectedRoute>
          } />
          <Route path="/fedex-files" element={
            <FeatureProtectedRoute requiredFeature="fedexFileUpload">
              <FedexFiles />
            </FeatureProtectedRoute>
          } />
          <Route path="/errors" element={
            <FeatureProtectedRoute requiredFeature="netsuiteErrors">
              <Errors />
            </FeatureProtectedRoute>
          } />
          <Route path="/netsuite-setup" element={
            <FeatureProtectedRoute requiredFeature="locations">
              <NetsuiteSetup />
            </FeatureProtectedRoute>
          } />
          <Route path="/item-sales-by-customer" element={
            <FeatureProtectedRoute requiredFeature="itemSalesByCus">
              <ItemSalesByCustomer />
            </FeatureProtectedRoute>
          } />
          <Route path="/item-sales-report" element={
            <FeatureProtectedRoute requiredFeature="itemSalesByCus">
              <ItemSalesReport />
            </FeatureProtectedRoute>
          } />
          <Route path="/insufficient-inventory" element={
            <FeatureProtectedRoute requiredFeature="ltlInsufficientInventory">
              <InsufficientInventory />
            </FeatureProtectedRoute>
          } />
          <Route path="/ship-confirm-by-status" element={
            <FeatureProtectedRoute requiredFeature="regularDashboard">
              <ShipConfirmByStatus />
            </FeatureProtectedRoute>
          } />
          <Route path="/ship-tracking" element={
            <FeatureProtectedRoute requiredFeature="regularDashboard">
              <ShipTracking />
            </FeatureProtectedRoute>
          } />
          <Route path="/amazon-amxl-dashboard" element={
            <FeatureProtectedRoute requiredFeature="amAmxlDashboard">
              <AmazonAmxlDashboard />
            </FeatureProtectedRoute>
          } />
          <Route path="/ats-dashboard" element={
            <FeatureProtectedRoute requiredFeature="amAtsDashboard">
              <AtsDashboard />
            </FeatureProtectedRoute>
          } />
          <Route path="/ups-dashboard" element={
            <FeatureProtectedRoute requiredFeature="amUpsDashboard">
              <UpsDashboard />
            </FeatureProtectedRoute>
          } />
          <Route path="/submitted-pallet-list" element={
            <SuperAdminRoute>
              <SubmittedPalletList />
            </SuperAdminRoute>
          } />
          <Route path="/container-label" element={
            <SuperAdminRoute>
              <ContainerLabel />
            </SuperAdminRoute>
          } />
          <Route path="/bulk-sales-order-priority" element={
            <FeatureProtectedRoute requiredFeature="bulkSopriority">
              <BulkSalesOrderPriority />
            </FeatureProtectedRoute>
          } />
          <Route path="/ship-errors" element={
            <FeatureProtectedRoute requiredFeature="shippingLabelErrors">
              <ShipErrors />
            </FeatureProtectedRoute>
          } />
          <Route path="/marketplace-price-update" element={
            <FeatureProtectedRoute requiredFeature="UpdateMarketplacePricing">
              <MarketplacePriceUpdate />
            </FeatureProtectedRoute>
          } />

          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all other routes and redirect to dashboard for security */}
          <Route path="*" element={
            <PrivateRoute>
              <Navigate to="/dashboard" replace />
            </PrivateRoute>
          } />
        </Routes>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default MainApp;